import React, {useEffect, useMemo, useState} from "react";
import {Auth} from "aws-amplify";
import Form from "react-bootstrap/Form";
import LoaderButton from "../components/LoaderButton";
import {useAppContext} from "../libs/contextLib";
import {useFormFields} from "../libs/hooksLib";
import "./Login.css";
import {Link, useHistory, useParams} from "react-router-dom";
import {setUserOperator} from "../services/routeService";
import {message} from 'antd';
import ReactGA from "react-ga4";
import {routesModelExpiryService} from "../services/ModelService";
import {checkOnline} from "../libs/errorLib";

function Login() {

    const {id} = useParams();
    const history = useHistory();
    const {userHasAuthenticated, setApiKey} = useAppContext();
    const [isLoading, setIsLoading] = useState(false);
    const [fields, handleFieldChange] = useFormFields({
        email: "",
        password: ""
    });
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const [messageApi, contextHolder] = message.useMessage();
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState("");
    const [mfaUser, setMfaUser] = useState(null);

    function validateForm() {
        return fields.email.length > 0 && fields.password.length > 0;
    }

    useEffect(() => {
        async function onLoad() {
            try {
                console.log('Loading login...')
                const user = await Auth.currentUserInfo();
                if (user?.attributes) {
                    console.log('Already logged in...');
                    history.push('/');
                } else {
                    console.log('Not logged in.')
                }
                if (searchParams.get("c") === 'se') {
                    messageApi.info('You have been logged out of this session because your account is being used on another device.', 10)
                }
            } catch (e) {
                console.log("Couldn't get user attributes: "+e, e);
            }
        }

        onLoad().then(() => {
            console.log('Login page loaded.');
            ReactGA.send({ hitType: "pageview", page: "/", title: "login" });
        });
    }, [history, searchParams, messageApi])

    async function handleSubmit(event) {
        event.preventDefault();
        setIsLoading(true);
        try {
            if (!await checkOnline()) {
                messageApi.open({
                    type: 'error',
                    content: 'You appear to be offline. Please check your internet connection and try again.',
                });
                setIsLoading(false);
                return
            }
            const user = await Auth.signIn(fields.email?.toLowerCase(), fields.password);
            if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
                setMfaRequired(true);
                setMfaUser(user);
                setIsLoading(false);
                return;
            }
            if (user?.attributes['custom:isAdmin'] !== 'true') {
                console.log("Logged in before global logout.")
                await Auth.signOut({global: true});
                console.log("Global logout done.")
                await new Promise(r => setTimeout(r, 2000));
                const user2 = await Auth.signIn(fields.email?.toLowerCase(), fields.password);
                if (user2.challengeName === 'SMS_MFA' || user2.challengeName === 'SOFTWARE_TOKEN_MFA') {
                    setMfaRequired(true);
                    setMfaUser(user2);
                    setIsLoading(false);
                    return;
                }
            }
            userHasAuthenticated(true);
            if (id) {
                console.log('Setting operator: ', id)
                try {
                    await setUserOperator(id);
                } catch (e) {
                    console.log("Couldn't set user operator: "+e, e);
                    window.location.reload();
                }
            }
            let key = id || (await Auth.currentUserInfo()).attributes['custom:operatorId']
            console.log(`Found key: ${key}`);
            setApiKey(key);
            await routesModelExpiryService.deleteAllDbs();
            console.log('Reloading page...')
            window.location.reload();
        } catch (e) {
            console.log(e, e)
            messageApi.open({
                type: 'error',
                content: e + '. Please try again.',
                duration: 0
            });
            userHasAuthenticated(false);
            setIsLoading(false);
        }
    }

    async function handleMfaSubmit(event) {
        event.preventDefault();
        setIsLoading(true);
        try {
            const loggedInUser = await Auth.confirmSignIn(mfaUser, mfaCode, mfaUser.challengeName);
            userHasAuthenticated(true);
            if (id) {
                try {
                    await setUserOperator(id);
                } catch (e) {
                    window.location.reload();
                }
            }
            let key = id || (await Auth.currentUserInfo()).attributes['custom:operatorId']
            setApiKey(key);
            await routesModelExpiryService.deleteAllDbs();
            console.log('Reloading page...')
            window.location.reload();
        } catch (e) {
            messageApi.open({
                type: 'error',
                content: e + '. Please try again.',
                duration: 0
            });
            setIsLoading(false);
        }
    }

    function renderMfaForm() {
        return (
            <div className="Login">
                {contextHolder}
                <Form onSubmit={handleMfaSubmit}>
                    <Form.Group size="lg" controlId="mfaCode">
                        <Form.Label>Enter the code sent to your phone</Form.Label>
                        <Form.Control
                            autoFocus
                            type="text"
                            value={mfaCode}
                            onChange={e => setMfaCode(e.target.value)}
                        />
                    </Form.Group>
                    <LoaderButton
                        block
                        size="lg"
                        type="submit"
                        isLoading={isLoading}
                        disabled={!mfaCode}
                    >
                        Verify Code
                    </LoaderButton>
                </Form>
            </div>
        );
    }

    return (
        mfaRequired ? renderMfaForm() : (
            <div className="Login">
                {contextHolder}
                <Form onSubmit={handleSubmit}>
                    <Form.Group size="lg" controlId="email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            autoFocus
                            type="email"
                            value={fields.email}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                    <Form.Group size="lg" controlId="password">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                            type="password"
                            value={fields.password}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                    <Link to="/reset">Forgot password?</Link>
                    <LoaderButton
                        block
                        size="lg"
                        type="submit"
                        isLoading={isLoading}
                        disabled={!validateForm()}
                    >
                        Login
                    </LoaderButton>
                </Form>
            </div>
        )
    );
}

export default React.memo(Login)
